import React, { useCallback, useEffect, useRef, useState } from "react";
import "./StorageTab.css";
import { NumberInput } from "src/components/Input/Number/Number";
import HorizontalTabs from "src/components/HorizontalTabs/HorizontalTabs";
import SelectUnselectExtended from "src/components/Input/SelectUnselectExtended/SelectUnselectExtended";
import InlineToggle, {
  InlineToggleOption,
} from "src/components/Input/InlineToggle/InlineToggle";
import { ToggleSwitch } from "src/components/ToggleSwitch/ToggleSwitch";
import { useConfiguration } from "src/contexts/ConfigurationContext";
import WallDoorWindow from "src/components/WallDoorWindow/WallDoorWindow";
import { Dimensions, Wall } from "src/types/configuration";
// @ts-ignore
import isEqual from "lodash/isEqual";
import { WALL_FINISH_VARIANTS } from "src/helpers/wallFinishes";
import ColorSelect from "src/components/Input/Select/ColorSelect";
import { doors } from "src/components/Input/Select/DoorSelect";
import { windows } from "src/components/Input/Select/WindowSelect";
import Checkbox from "src/components/Input/Checkbox/Checkbox";
import { isFlatRoofType } from "src/helpers/roofType";

const updateDimensions = (
  roofType: string,
  dimensions: Dimensions,
  leftStorage: boolean,
  rightStorage: boolean,
  setDimensions: (dimensions: Dimensions) => void,
  leftStorageWidth = 0,
  rightStorageWidth = 0
) => {
  leftStorageWidth =
    leftStorageWidth === 0 ? dimensions.leftStorageWidth : leftStorageWidth;
  rightStorageWidth =
    rightStorageWidth === 0 ? dimensions.rightStorageWidth : rightStorageWidth;
  let width = dimensions.width;

  let storagesWidth = 0;

  // if (leftStorage && rightStorage) {
  //     storagesWidth = roofType === 'platdak' ? 150 * 3 : 120 * 3; // 3 * column width
  // }

  if (leftStorage) {
    storagesWidth += leftStorageWidth;
  }

  if (rightStorage) {
    storagesWidth += rightStorageWidth;
  }

  if (storagesWidth > 0) {
    storagesWidth -= 150;
  }

  if (width <= storagesWidth) {
    width = storagesWidth;
  }

  if (width > 8000) {
    width = 8000;
  }

  const newDimensions = {
    ...dimensions,
    leftStorageWidth: leftStorageWidth,
    rightStorageWidth: rightStorageWidth,
    width: width,
  };

  if (!isEqual(newDimensions, dimensions)) setDimensions(newDimensions);
};

const getMaxStorageSize = (side: string, dimensions: Dimensions) => {
  const maxWidth = 8000;
  if (side === "right") {
    return dimensions.width >= maxWidth && dimensions.leftStorageWidth === 4000
      ? 3500
      : maxWidth - dimensions.leftStorageWidth > 4000
        ? 4000
        : maxWidth - dimensions.leftStorageWidth;
  }

  return dimensions.width >= maxWidth && dimensions.rightStorageWidth === 4000
    ? 3500
    : maxWidth - dimensions.rightStorageWidth > 4000
      ? 4000
      : maxWidth - dimensions.rightStorageWidth;
};

const storageDescription = "";

const getFinishLabel = (finish?: string) => {
  if (!finish || finish === "empty") return "";
  return (
    WALL_FINISH_VARIANTS.find((variant) => variant.value === finish)?.label ||
    ""
  );
};

const shouldClearInnerWoodColor = (outerColor: string) =>
  outerColor.startsWith("wall_blockhut");

const getStorageSummary = (
  walls: Array<Wall | undefined>,
  terraceWall?: Wall,
  terraceEnabled?: boolean
) => {
  const summaryParts: string[] = [];
  const primaryWall = walls.find((wall) => wall);

  if (primaryWall) {
    const outerLabel = getFinishLabel(primaryWall.woodWall.outerColor);
    if (outerLabel) summaryParts.push(`Buitenwand - ${outerLabel}`);

    const innerLabel = getFinishLabel(primaryWall.woodWall.innerColor);
    if (innerLabel) summaryParts.push(`Binnenwand - ${innerLabel}`);
  }

  if (terraceEnabled && terraceWall) {
    const terraceLabel = getFinishLabel(terraceWall.woodWall.outerColor);
    if (terraceLabel) summaryParts.push(`Luifelzijde - ${terraceLabel}`);
  }

  const collectDoorWindow = (wall?: Wall) => {
    if (!wall) return;
    if (wall.woodWall.door && wall.woodWall.door !== "empty") {
      const label =
        doors.find((d) => d.value === wall.woodWall.door)?.name || "";
      if (label) summaryParts.push(`Deur - ${label}`);
    }
    if (wall.woodWall.window && wall.woodWall.window !== "empty") {
      const label =
        windows.find((w) => w.value === wall.woodWall.window)?.name || "";
      if (label) summaryParts.push(`Raam - ${label}`);
    }
  };

  walls.forEach(collectDoorWindow);
  if (terraceEnabled) {
    collectDoorWindow(terraceWall);
  }

  return summaryParts.join("; ");
};

interface ColorTabsProps {
  outerValue?: string;
  innerValue?: string;
  onOuterChange: (value: string) => void;
  onInnerChange: (value: string) => void;
  activeTab: number;
  onTabChange: (tabId: number) => void;
}

const ColorTabs: React.FC<ColorTabsProps> = ({
  outerValue,
  innerValue,
  onOuterChange,
  onInnerChange,
  activeTab,
  onTabChange,
}) => {
  const isInnerColorDisabled =
    outerValue === "empty" || !!outerValue?.includes("blockhut");

  const tabs = [
    {
      id: 1,
      title: "Buitenzijde",
      component: <ColorSelect value={outerValue} onChange={onOuterChange} />,
    },
    {
      id: 2,
      title: "Binnenzijde",
      disabled: isInnerColorDisabled,
      component: (
        <ColorSelect
          value={innerValue}
          onChange={onInnerChange}
          innerColor
          disabled={isInnerColorDisabled}
        />
      ),
    },
  ];

  return (
    <HorizontalTabs
      className={"storage-color-inner-tabs"}
      tabs={tabs}
      value={activeTab}
      onChange={onTabChange}
    />
  );
};

const StorageTab: React.FC = () => {
  const {
    state,
    configurator,
    setDimensions,
    addWall,
    setWalls,
    setStorages,
    leftStorageColorTab,
    setLeftStorageColorTab,
    rightStorageColorTab,
    setRightStorageColorTab,
  } = useConfiguration();

  const [leftActiveTab, setLeftActiveTab] = useState<number>(1);
  const [rightActiveTab, setRightActiveTab] = useState<number>(1);
  const [leftStorageInnerColorTab, setLeftStorageInnerColorTab] =
    useState<number>(1);
  const [rightStorageInnerColorTab, setRightStorageInnerColorTab] =
    useState<number>(1);
  const [leftMainTab, setLeftMainTab] = useState<number>(1);
  const [rightMainTab, setRightMainTab] = useState<number>(1);
  const [showLeftDoorWindowComponent, setShowLeftDoorWindowComponent] =
    useState(false);
  const [showRightDoorWindowComponent, setShowRightDoorWindowComponent] =
    useState(false);
  const [showLeftStorageComponent, setShowLeftStorageComponent] =
    useState(false);
  const [showRightStorageComponent, setShowRightStorageComponent] =
    useState(false);
  const pendingStorageFocusRef = useRef({ left: false, right: false });

  const leftFrontWall = state.walls.find(
    (wall: Wall) => wall.storage === "left_front"
  );
  const leftRightWall = state.walls.find(
    (wall: Wall) => wall.storage === "left_right"
  );
  const leftBackWall = state.walls.find(
    (wall: Wall) => wall.storage === "left_back"
  );
  const leftLeftWall = state.walls.find(
    (wall: Wall) => wall.storage === "left_left"
  );
  const rightFrontWall = state.walls.find(
    (wall: Wall) => wall.storage === "right_front"
  );
  const rightLeftWall = state.walls.find(
    (wall: Wall) => wall.storage === "right_left"
  );
  const rightBackWall = state.walls.find(
    (wall: Wall) => wall.storage === "right_back"
  );
  const rightRightWall = state.walls.find(
    (wall: Wall) => wall.storage === "right_right"
  );

  const handleLeftStorageEnable = (enabled: boolean) => {
    if (state.storages.left === enabled) return;
    configurator.deselectWalls();
    pendingStorageFocusRef.current.left = enabled;
    updateDimensions(
      state.roofType,
      state.dimensions,
      enabled,
      state.storages.right,
      setDimensions
    );
    setStorages({
      left: enabled,
      right: state.storages.right,
      leftLuifelEnabled: enabled
        ? state.storages.leftLuifelEnabled ?? false
        : false,
      rightLuifelEnabled: state.storages.rightLuifelEnabled,
    });
    if (!enabled) {
      setShowLeftDoorWindowComponent(false);
      setShowLeftStorageComponent(false);
    }
  };

  const handleRightStorageEnable = (enabled: boolean) => {
    if (state.storages.right === enabled) return;
    configurator.deselectWalls();
    pendingStorageFocusRef.current.right = enabled;
    updateDimensions(
      state.roofType,
      state.dimensions,
      state.storages.left,
      enabled,
      setDimensions
    );
    setStorages({
      left: state.storages.left,
      right: enabled,
      leftLuifelEnabled: state.storages.leftLuifelEnabled,
      rightLuifelEnabled: enabled
        ? state.storages.rightLuifelEnabled ?? false
        : false,
    });
    if (!enabled) {
      setShowRightDoorWindowComponent(false);
      setShowRightStorageComponent(false);
    }
  };

  const handleLeftStorageOuterColorChange = (outerColor: string) => {
    // Use global state for luifel enabled
    const luifelEnabled = state.storages.leftLuifelEnabled ?? false;

    const updatedWalls = state.walls.map((wall: Wall) => {
      if (!wall.storage.startsWith("left")) {
        return wall;
      }

      // Update storage walls (excluding luifel wall)
      if (wall.storage !== "left_right") {
        const woodWall = { ...wall.woodWall, outerColor: outerColor };

        if (shouldClearInnerWoodColor(outerColor)) {
          woodWall.innerColor = "empty";
        }

        return { ...wall, woodWall };
      }

      // If luifel is disabled ("Niet"), sync it with outer wall
      if (wall.storage === "left_right" && !luifelEnabled) {
        const woodWall = { ...wall.woodWall, outerColor: outerColor };

        if (shouldClearInnerWoodColor(outerColor)) {
          woodWall.innerColor = "empty";
        }

        return { ...wall, woodWall };
      }

      return wall;
    });

    if (!isEqual(updatedWalls, state.walls)) setWalls(updatedWalls);
  };

  const handleLeftStorageInnerColorChange = (innerColor: string) => {
    const luifelEnabled = state.storages.leftLuifelEnabled ?? false;
    const updatedWalls = state.walls.map((wall: Wall) => {
      if (!wall.storage.startsWith("left")) {
        return wall;
      }

      if (luifelEnabled && wall.storage === "left_right") {
        return wall;
      }

      const woodWall = { ...wall.woodWall, innerColor };
      return { ...wall, woodWall };
    });

    if (!isEqual(updatedWalls, state.walls)) setWalls(updatedWalls);
  };

  const handleLeftStorageOtherInnerColorChange = (innerColor: string) => {
    const updatedWalls = state.walls.map((wall: Wall) => {
      if (wall.storage !== "left_right") {
        return wall;
      }

      const woodWall = { ...wall.woodWall, innerColor };
      return { ...wall, woodWall };
    });

    if (!isEqual(updatedWalls, state.walls)) setWalls(updatedWalls);
  };

  const handleLeftStorageOtherColorChange = (otherColor: string) => {
    const updatedWalls = state.walls.map((wall: Wall) => {
      if (wall.storage !== "left_right") {
        return wall;
      }

      const woodWall = { ...wall.woodWall, outerColor: otherColor };

      if (shouldClearInnerWoodColor(otherColor)) {
        woodWall.innerColor = "empty";
      }

      return { ...wall, woodWall };
    });

    if (!isEqual(updatedWalls, state.walls)) setWalls(updatedWalls);
  };

  const handleRightStorageInnerColorChange = (innerColor: string) => {
    const luifelEnabled = state.storages.rightLuifelEnabled ?? false;
    const updatedWalls = state.walls.map((wall: Wall) => {
      if (!wall.storage.startsWith("right")) {
        return wall;
      }

      if (luifelEnabled && wall.storage === "right_left") {
        return wall;
      }

      const woodWall = { ...wall.woodWall, innerColor };
      return { ...wall, woodWall };
    });

    if (!isEqual(updatedWalls, state.walls)) setWalls(updatedWalls);
  };

  const handleRightStorageOtherInnerColorChange = (innerColor: string) => {
    const updatedWalls = state.walls.map((wall: Wall) => {
      if (wall.storage !== "right_left") {
        return wall;
      }

      const woodWall = { ...wall.woodWall, innerColor };
      return { ...wall, woodWall };
    });

    if (!isEqual(updatedWalls, state.walls)) setWalls(updatedWalls);
  };

  const handleRightStorageOuterColorChange = (outerColor: string) => {
    // Use global state for luifel enabled
    const luifelEnabled = state.storages.rightLuifelEnabled ?? false;

    const updatedWalls = state.walls.map((wall: Wall) => {
      if (!wall.storage.startsWith("right")) {
        return wall;
      }

      // Update storage walls (excluding luifel wall)
      if (wall.storage !== "right_left") {
        const woodWall = { ...wall.woodWall, outerColor: outerColor };

        if (shouldClearInnerWoodColor(outerColor)) {
          woodWall.innerColor = "empty";
        }

        return { ...wall, woodWall };
      }

      // If luifel is disabled ("Niet"), sync it with outer wall
      if (wall.storage === "right_left" && !luifelEnabled) {
        const woodWall = { ...wall.woodWall, outerColor: outerColor };

        if (shouldClearInnerWoodColor(outerColor)) {
          woodWall.innerColor = "empty";
        }

        return { ...wall, woodWall };
      }

      return wall;
    });

    if (!isEqual(updatedWalls, state.walls)) setWalls(updatedWalls);
  };

  const handleRightStorageOtherColorChange = (otherColor: string) => {
    const updatedWalls = state.walls.map((wall: Wall) => {
      if (wall.storage !== "right_left") {
        return wall;
      }

      const woodWall = { ...wall.woodWall, outerColor: otherColor };

      if (shouldClearInnerWoodColor(otherColor)) {
        woodWall.innerColor = "empty";
      }

      return { ...wall, woodWall };
    });

    if (!isEqual(updatedWalls, state.walls)) setWalls(updatedWalls);
  };

  const handleLeftStorageWidth = (width: number) => {
    updateDimensions(
      state.roofType,
      state.dimensions,
      state.storages.left,
      state.storages.right,
      setDimensions,
      width
    );
  };

  const handleRightStorageWidth = (width: number) => {
    updateDimensions(
      state.roofType,
      state.dimensions,
      state.storages.left,
      state.storages.right,
      setDimensions,
      0,
      width
    );
  };

  const leftSidesTabs = [
    {
      id: 1,
      title: "VOOR",
      component: (
        <WallDoorWindow
          key={"left-storage-front-wall-tab"}
          wallName={"left_front"}
          onWallChange={addWall}
          showComponent={showLeftDoorWindowComponent}
          setShowComponent={setShowLeftDoorWindowComponent}
        />
      ),
    },
    {
      id: 2,
      title: "ACHTER",
      component: (
        <WallDoorWindow
          key={"left-storage-back-wall-tab"}
          wallName={"left_back"}
          onWallChange={addWall}
          showComponent={showLeftDoorWindowComponent}
          setShowComponent={setShowLeftDoorWindowComponent}
        />
      ),
    },
    {
      id: 3,
      title: "RECHTS",
      component: (
        <WallDoorWindow
          key={`left-storage-right-wall-tab-${leftRightWall?.woodWall.door}-${leftRightWall?.woodWall.doorSide}-${leftRightWall?.woodWall.window}`}
          wallName={"left_right"}
          onWallChange={addWall}
          showComponent={showLeftDoorWindowComponent}
          setShowComponent={setShowLeftDoorWindowComponent}
        />
      ),
    },
    {
      id: 4,
      title: "LINKS",
      component: (
        <WallDoorWindow
          key={"left-storage-left-wall-tab"}
          wallName={"left_left"}
          onWallChange={addWall}
          showComponent={showLeftDoorWindowComponent}
          setShowComponent={setShowLeftDoorWindowComponent}
        />
      ),
    },
  ];

  const rightSidesTabs = [
    {
      id: 1,
      title: "VOOR",
      component: (
        <WallDoorWindow
          key={"right-storage-front-wall-tab"}
          wallName={"right_front"}
          onWallChange={addWall}
          showComponent={showRightDoorWindowComponent}
          setShowComponent={setShowRightDoorWindowComponent}
        />
      ),
    },
    {
      id: 2,
      title: "ACHTER",
      component: (
        <WallDoorWindow
          key={"right-storage-back-wall-tab"}
          wallName={"right_back"}
          onWallChange={addWall}
          showComponent={showRightDoorWindowComponent}
          setShowComponent={setShowRightDoorWindowComponent}
        />
      ),
    },
    {
      id: 3,
      title: "RECHTS",
      component: (
        <WallDoorWindow
          key={"right-storage-right-wall-tab"}
          wallName={"right_right"}
          onWallChange={addWall}
          showComponent={showRightDoorWindowComponent}
          setShowComponent={setShowRightDoorWindowComponent}
        />
      ),
    },
    {
      id: 4,
      title: "LINKS",
      component: (
        <WallDoorWindow
          key={`right-storage-left-wall-tab-${rightLeftWall?.woodWall.door}-${rightLeftWall?.woodWall.doorSide}-${rightLeftWall?.woodWall.window}`}
          wallName={"right_left"}
          onWallChange={addWall}
          showComponent={showRightDoorWindowComponent}
          setShowComponent={setShowRightDoorWindowComponent}
        />
      ),
    },
  ];

  const focusStorageWall = useCallback(
    (wall?: Wall) => {
      if (!wall) return;
      configurator.focusOnWall(wall);
      configurator.selectWall(wall);
    },
    [configurator]
  );

  const focusLeftWallHighlight = useCallback(
    (id: number) => {
      if (id === 1) focusStorageWall(leftFrontWall);
      if (id === 2) focusStorageWall(leftBackWall);
      if (id === 3) focusStorageWall(leftRightWall);
      if (id === 4) focusStorageWall(leftLeftWall);
    },
    [focusStorageWall, leftBackWall, leftFrontWall, leftLeftWall, leftRightWall]
  );

  const focusRightWallHighlight = useCallback(
    (id: number) => {
      if (id === 1) focusStorageWall(rightFrontWall);
      if (id === 2) focusStorageWall(rightBackWall);
      if (id === 3) focusStorageWall(rightRightWall);
      if (id === 4) focusStorageWall(rightLeftWall);
    },
    [
      focusStorageWall,
      rightBackWall,
      rightFrontWall,
      rightLeftWall,
      rightRightWall,
    ]
  );

  // Reset tabs when roof type changes
  useEffect(() => {
    const leftFilteredTabs = leftSidesTabs.filter((item) => {
      return (
        isFlatRoofType(state.roofType) ||
        (state.roofType === "kapschuur" && item.title !== "ACHTER")
      );
    });
    const rightFilteredTabs = rightSidesTabs.filter((item) => {
      return (
        isFlatRoofType(state.roofType) ||
        (state.roofType === "kapschuur" && item.title !== "ACHTER")
      );
    });

    if (!leftFilteredTabs.find((tab) => tab.id === leftActiveTab)) {
      setLeftActiveTab(leftFilteredTabs[0]?.id || 1);
    }
    if (!rightFilteredTabs.find((tab) => tab.id === rightActiveTab)) {
      setRightActiveTab(rightFilteredTabs[0]?.id || 1);
    }
  }, [state.roofType, leftActiveTab, rightActiveTab]);

  useEffect(() => {
    const pendingFocus = pendingStorageFocusRef.current;

    if (pendingFocus.left) {
      if (!state.storages.left) {
        pendingFocus.left = false;
      } else if (leftFrontWall) {
        configurator.focusOnWall(leftFrontWall);
        pendingFocus.left = false;
      }
    }

    if (pendingFocus.right) {
      if (!state.storages.right) {
        pendingFocus.right = false;
      } else if (rightFrontWall) {
        configurator.focusOnWall(rightFrontWall);
        pendingFocus.right = false;
      }
    }
  }, [
    configurator,
    leftFrontWall,
    rightFrontWall,
    state.storages.left,
    state.storages.right,
  ]);

  // Handle storage highlighting after synchronization completes
  useEffect(() => {
    // Determine which storage should be highlighted
    const shouldHighlightLeft = showLeftStorageComponent && state.storages.left;
    const shouldHighlightRight =
      showRightStorageComponent && state.storages.right;

    // If no storage should be highlighted, deselect
    if (!shouldHighlightLeft && !shouldHighlightRight) {
      configurator.deselectBerging();
      return;
    }

    // Wait for synchronization to complete (useSynchronizeServices runs every 700ms)
    // Add buffer time for house update to complete
    const timeoutId = setTimeout(() => {
      if (shouldHighlightLeft) {
        configurator.selectBerging("left");
      } else {
        configurator.deselectBerging("left");
      }

      if (shouldHighlightRight) {
        configurator.selectBerging("right");
      } else {
        configurator.deselectBerging("right");
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [
    state.storages.left,
    state.storages.right,
    state.dimensions.leftStorageWidth,
    state.dimensions.rightStorageWidth,
    showLeftStorageComponent,
    showRightStorageComponent,
    configurator,
  ]);

  useEffect(() => {
    if (leftMainTab === 1) {
      configurator.deselectWalls();
      if (showLeftDoorWindowComponent) {
        setShowLeftDoorWindowComponent(false);
      }
      return;
    }

    if (!showLeftDoorWindowComponent) {
      setShowLeftDoorWindowComponent(true);
    }

    focusLeftWallHighlight(leftActiveTab);
  }, [
    configurator,
    focusLeftWallHighlight,
    leftActiveTab,
    leftMainTab,
    showLeftDoorWindowComponent,
  ]);

  useEffect(() => {
    if (rightMainTab === 1) {
      configurator.deselectWalls();
      if (showRightDoorWindowComponent) {
        setShowRightDoorWindowComponent(false);
      }
      return;
    }

    if (!showRightDoorWindowComponent) {
      setShowRightDoorWindowComponent(true);
    }

    focusRightWallHighlight(rightActiveTab);
  }, [
    configurator,
    focusRightWallHighlight,
    rightActiveTab,
    rightMainTab,
    showRightDoorWindowComponent,
  ]);

  const handleLeftFocusOnWall = (id: number) => {
    setLeftActiveTab(id);
    focusLeftWallHighlight(id);
  };

  const handleRightFocusOnWall = (id: number) => {
    setRightActiveTab(id);
    focusRightWallHighlight(id);
  };

  const StorageDoneButton: React.FC<{
    label: string;
    onClick: () => void;
  }> = ({ label, onClick }) => {
    return (
      <button
        type="button"
        className="storage-complete-button"
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        {label}
      </button>
    );
  };

  const StorageDisableButton: React.FC<{
    label: string;
    onClick: () => void;
  }> = ({ label, onClick }) => {
    return (
      <button
        type="button"
        className="storage-disable-button"
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        {label}
      </button>
    );
  };

  const leftStorageSummary = getStorageSummary(
    [leftFrontWall, leftBackWall, leftLeftWall, leftRightWall],
    leftRightWall,
    state.storages.leftLuifelEnabled
  );

  const handleLeftLuifelEnabledChange = (enabled: boolean) => {
    setStorages({
      left: state.storages.left,
      right: state.storages.right,
      leftLuifelEnabled: enabled,
      rightLuifelEnabled: state.storages.rightLuifelEnabled,
    });
  };

  const handleLeftEditableTerraceChange = (enabled: boolean) => {
    const current = state.storages.leftLuifelEnabled ?? false;
    if (enabled === current) return;

    if (!enabled) {
      const outerColor =
        leftFrontWall?.woodWall.outerColor ?? "wall_potdeksel_black";
      handleLeftStorageOtherColorChange(outerColor);
    }

    handleLeftLuifelEnabledChange(enabled);
  };

  const LeftStorageColorComponent: React.FC = () => {
    const terraceEnabled = state.storages.leftLuifelEnabled ?? false;
    const frontOuter = leftFrontWall?.woodWall.outerColor;
    const frontInner = leftFrontWall?.woodWall.innerColor;
    const terraceOuter = leftRightWall?.woodWall.outerColor;
    const terraceInner = leftRightWall?.woodWall.innerColor;

    useEffect(() => {
      if (!terraceEnabled && leftStorageColorTab !== 1) {
        setLeftStorageColorTab(1);
      }
    }, [terraceEnabled, leftStorageColorTab, setLeftStorageColorTab]);

    if (!terraceEnabled) {
      return (
        <ColorTabs
          outerValue={frontOuter}
          innerValue={frontInner}
          onOuterChange={handleLeftStorageOuterColorChange}
          onInnerChange={handleLeftStorageInnerColorChange}
          activeTab={leftStorageInnerColorTab}
          onTabChange={setLeftStorageInnerColorTab}
        />
      );
    }

    const tabs = [
      {
        id: 1,
        title: "Voor+Achter+Links",
        component: (
          <ColorTabs
            outerValue={frontOuter}
            innerValue={frontInner}
            onOuterChange={handleLeftStorageOuterColorChange}
            onInnerChange={handleLeftStorageInnerColorChange}
            activeTab={leftStorageInnerColorTab}
            onTabChange={setLeftStorageInnerColorTab}
          />
        ),
      },
      {
        id: 2,
        title: "Luifelzijde",
        component: (
          <ColorTabs
            outerValue={terraceOuter}
            innerValue={terraceInner ?? frontInner}
            onOuterChange={handleLeftStorageOtherColorChange}
            onInnerChange={handleLeftStorageOtherInnerColorChange}
            activeTab={leftStorageInnerColorTab}
            onTabChange={setLeftStorageInnerColorTab}
          />
        ),
      },
    ];

    return (
      <HorizontalTabs
        className={"storage-color-terras-tabs"}
        tabs={tabs}
        value={leftStorageColorTab}
        onChange={setLeftStorageColorTab}
      />
    );
  };

  const LeftStorageDoorWindowComponent: React.FC = () => {
    const filteredTabs = leftSidesTabs.filter((item) => {
      return (
        isFlatRoofType(state.roofType) ||
        (state.roofType === "kapschuur" && item.title !== "ACHTER")
      );
    });

    // Ensure activeTab is valid for filtered tabs
    const validLeftTab = filteredTabs.find((tab) => tab.id === leftActiveTab)
      ? leftActiveTab
      : filteredTabs[0]?.id || 1;

    return (
      <HorizontalTabs
        key={`left-storage-door-window-tabs`}
        tabs={filteredTabs}
        className={"storage-side-tabs"}
        value={validLeftTab}
        onChange={handleLeftFocusOnWall}
      />
    );
  };

  const LeftStorageComponent: React.FC = () => {
    const tabs = [
      {
        id: 1,
        title: "Wanden",
        component: <LeftStorageColorComponent />,
      },
      {
        id: 2,
        title: "Deur & raam",
        component: <LeftStorageDoorWindowComponent />,
      },
    ];

    return (
      <>
        <NumberInput
          key={"left-storage-width"}
          label="Breedte"
          // subStep={10}
          value={state.dimensions.leftStorageWidth}
          onChange={handleLeftStorageWidth}
          min={1500}
          max={getMaxStorageSize("left", state.dimensions)}
          step={100}
          unit={"cm"}
        />
        <Checkbox
          label={"Een ander wandtype voor de Luifelzijde"}
          checked={!!state.storages.leftLuifelEnabled}
          onChange={handleLeftEditableTerraceChange}
        />
        <HorizontalTabs
          tabs={tabs}
          className={"storage-card-tabs"}
          value={leftMainTab}
          onChange={(value) => setLeftMainTab(value)}
        />
        <div className="storage-action-row">
          <StorageDisableButton
            key={"left-storage-disable"}
            label="Linker berging verwijderen"
            onClick={() => handleLeftStorageEnable(false)}
          />
          <StorageDoneButton
            key={"left-storage-done"}
            label="Linker berging gereed"
            onClick={() => setShowLeftStorageComponent(false)}
          />
        </div>
      </>
    );
  };

  const rightStorageSummary = getStorageSummary(
    [rightFrontWall, rightBackWall, rightLeftWall, rightRightWall],
    rightLeftWall,
    state.storages.rightLuifelEnabled
  );

  const handleRightLuifelEnabledChange = (enabled: boolean) => {
    setStorages({
      left: state.storages.left,
      right: state.storages.right,
      leftLuifelEnabled: state.storages.leftLuifelEnabled,
      rightLuifelEnabled: enabled,
    });
  };

  const handleRightEditableTerraceChange = (enabled: boolean) => {
    const current = state.storages.rightLuifelEnabled ?? false;
    if (enabled === current) return;

    if (!enabled) {
      const outerColor =
        rightFrontWall?.woodWall.outerColor ?? "wall_potdeksel_black";
      handleRightStorageOtherColorChange(outerColor);
    }

    handleRightLuifelEnabledChange(enabled);
  };

  const RightStorageColorComponent: React.FC = () => {
    const terraceEnabled = state.storages.rightLuifelEnabled ?? false;
    const frontOuter = rightFrontWall?.woodWall.outerColor;
    const frontInner = rightFrontWall?.woodWall.innerColor;
    const terraceOuter = rightLeftWall?.woodWall.outerColor;
    const terraceInner = rightLeftWall?.woodWall.innerColor;

    useEffect(() => {
      if (!terraceEnabled && rightStorageColorTab !== 1) {
        setRightStorageColorTab(1);
      }
    }, [terraceEnabled, rightStorageColorTab, setRightStorageColorTab]);

    if (!terraceEnabled) {
      return (
        <ColorTabs
          outerValue={frontOuter}
          innerValue={frontInner}
          onOuterChange={handleRightStorageOuterColorChange}
          onInnerChange={handleRightStorageInnerColorChange}
          activeTab={rightStorageInnerColorTab}
          onTabChange={setRightStorageInnerColorTab}
        />
      );
    }

    const tabs = [
      {
        id: 1,
        title: "Voor+Achter+Rechts",
        component: (
          <ColorTabs
            outerValue={frontOuter}
            innerValue={frontInner}
            onOuterChange={handleRightStorageOuterColorChange}
            onInnerChange={handleRightStorageInnerColorChange}
            activeTab={rightStorageInnerColorTab}
            onTabChange={setRightStorageInnerColorTab}
          />
        ),
      },
      {
        id: 2,
        title: "Luifelzijde",
        component: (
          <ColorTabs
            outerValue={terraceOuter}
            innerValue={terraceInner ?? frontInner}
            onOuterChange={handleRightStorageOtherColorChange}
            onInnerChange={handleRightStorageOtherInnerColorChange}
            activeTab={rightStorageInnerColorTab}
            onTabChange={setRightStorageInnerColorTab}
          />
        ),
      },
    ];

    return (
      <HorizontalTabs
        className={"storage-color-terras-tabs"}
        tabs={tabs}
        value={rightStorageColorTab}
        onChange={setRightStorageColorTab}
      />
    );
  };

  const RightStorageDoorWindowComponent: React.FC = () => {
    const filteredTabs = rightSidesTabs.filter((item) => {
      return (
        isFlatRoofType(state.roofType) ||
        (state.roofType === "kapschuur" && item.title !== "ACHTER")
      );
    });

    // Ensure activeTab is valid for filtered tabs
    const validRightTab = filteredTabs.find((tab) => tab.id === rightActiveTab)
      ? rightActiveTab
      : filteredTabs[0]?.id || 1;

    return (
      <HorizontalTabs
        key={`right-storage-door-window-tabs`}
        tabs={filteredTabs}
        className={"storage-side-tabs"}
        value={validRightTab}
        onChange={handleRightFocusOnWall}
      />
    );
  };

  const RightStorageComponent: React.FC = () => {
    const tabs = [
      {
        id: 1,
        title: "Wanden",
        component: <RightStorageColorComponent />,
      },
      {
        id: 2,
        title: "Deur & raam",
        component: <RightStorageDoorWindowComponent />,
      },
    ];

    return (
      <>
        <NumberInput
          key={"right-storage-width"}
          label="Breedte"
          // subStep={10}
          value={state.dimensions.rightStorageWidth}
          onChange={handleRightStorageWidth}
          min={1500}
          max={getMaxStorageSize("right", state.dimensions)}
          step={100}
          unit={"cm"}
        />
        <Checkbox
          label={"Een ander wandtype voor de Luifelzijde"}
          checked={!!state.storages.rightLuifelEnabled}
          onChange={handleRightEditableTerraceChange}
        />
        <HorizontalTabs
          tabs={tabs}
          className={"storage-card-tabs"}
          value={rightMainTab}
          onChange={(value) => setRightMainTab(value)}
        />
        <div className="storage-action-row">
          <StorageDisableButton
            key={"right-storage-disable"}
            label="Rechter berging verwijderen"
            onClick={() => handleRightStorageEnable(false)}
          />
          <StorageDoneButton
            key={"right-storage-done"}
            label="Rechter berging gereed"
            onClick={() => setShowRightStorageComponent(false)}
          />
        </div>
      </>
    );
  };

  return (
    <div className="storage-tab">
      <SelectUnselectExtended
        key={"left-storage-enable"}
        name="Linker berging"
        imageUrl={`${process.env.PUBLIC_URL}/images/house_left.png`}
        description={storageDescription}
        Component={LeftStorageComponent}
        isSelected={state.storages.left}
        onSelect={() => {
          handleLeftStorageEnable(true);
        }}
        onUnselect={() => {
          handleLeftStorageEnable(false);
        }}
        showComponent={showLeftStorageComponent}
        onShowComponentToggle={(value) => {
          setLeftStorageInnerColorTab(1);
          setShowLeftStorageComponent(value);
        }}
        selectedNames={state.storages.left ? leftStorageSummary : ""}
        showCheckbox
        deselectByCheckboxOnly
      />
      <SelectUnselectExtended
        key={"right-storage-enable"}
        name="Rechter berging"
        imageUrl={`${process.env.PUBLIC_URL}/images/house_right.png`}
        description={storageDescription}
        Component={RightStorageComponent}
        isSelected={state.storages.right}
        onSelect={() => {
          handleRightStorageEnable(true);
        }}
        onUnselect={() => {
          handleRightStorageEnable(false);
        }}
        showComponent={showRightStorageComponent}
        onShowComponentToggle={(value) => {
          setRightStorageInnerColorTab(1);
          setShowRightStorageComponent(value);
        }}
        selectedNames={state.storages.right ? rightStorageSummary : ""}
        showCheckbox
        deselectByCheckboxOnly
      />
    </div>
  );
};

export default StorageTab;
